import {v4 as uuidv4} from "uuid";
import Logger from "../utils/Logger";
import PUMAMetricHelper from "../helper/PUMAMetricHelper";
import ThinClientProtocol from "../constants/ThinClientProtocol";
import {getThinClientUrl} from "./ThinClientUrl";

/**
 * Shared transport for the device-to-thin-client WebSocket.
 *
 * KEEP THIS FILE IDENTICAL ON `mainline` AND `mainlineStow`. See ThinClientUrl.js.
 *
 * Owns the connection concern only: which URL, opening and closing, the connect timeout,
 * reconnect backoff, and the ping/pong keepalive. It deliberately knows nothing about message
 * vocabulary -- each consumer layers its own protocol on top via {@link onMessage} and
 * {@link send}. Induct and Stow need genuinely different things there (request/response RPC
 * versus a fire-and-forget automation event feed), and an abstraction spanning both would fit
 * neither.
 *
 * Three options exist because hardcoding them to the first consumer would break the second:
 *
 *  - `shouldReconnect` is injected. The offline path only wants to reconnect while offline mode
 *    is active; the automation path wants to reconnect always.
 *  - `keepalive` is configurable. Stow gates its pings on a feature flag today.
 *  - `useRelay` is per-consumer. Only the offline path needs the native pinned-DNS relay, and
 *    routing online automation traffic through it would enlarge the blast radius for no gain.
 *
 * Those options are per-CONSUMER, not per-connection. All state below is module-scope and
 * {@link init} discards whatever the previous call configured, so ONE PAGE MUST NOT RUN TWO
 * TRANSPORTS FROM THIS MODULE -- the second init would tear the first one's socket down. That is
 * sufficient for what this replaces: each website has a single offline connection, and Stow's
 * automation feed deliberately stays on its own existing communicator rather than migrating here,
 * so no page configures this module twice. Serving two live sockets from one page would mean
 * making the transport instantiable, which is a larger change than either consumer needs.
 *
 * NOTHING happens at import time -- no socket, no timers, no storage reads. Every execution
 * website that depends on this package receives this module whether or not it wants a thin
 * client, so the module must stay inert until {@link connect} is called.
 */

/**
 * How long to wait for the socket to open before giving up.
 *
 * MUST stay ABOVE `OosFallbackRelayFactory.CONNECT_TIMEOUT_SECONDS` in StationBuddyFramework
 * (3s as of 0c59845). If the relay outlives our patience we abandon the socket before its
 * close frame arrives, and every upstream failure surfaces as a generic 1005/1006 instead of
 * a diagnostic 4001-4003. Nothing enforces the ordering across the two languages.
 */
const CONNECT_TIMEOUT_MS = 4000;

const RECONNECT_INTERVAL_MS = 1000;
const RECONNECT_MAX_INTERVAL_MS = 30000;
const PING_INTERVAL_MS = 1000;
const PONG_TIMEOUT_MS = 800;
const MAX_MISSED_PONGS = 2;

const {PROCESS_PATH, STATES, CLOSE_CODES, RELAY_URL} = ThinClientProtocol;

let config = null;
let socket = null;
let connectPromise = null;
let reconnectDelay = RECONNECT_INTERVAL_MS;
let pingInterval = null;
let pongTimeout = null;
let latestPingRequestId = null;
let missedPongCount = 0;
let intentionalClose = false;
/**
 * Handle for a reconnect waiting out its backoff, so close() can cancel it.
 *
 * Needed because by the time a reconnect is pending, cleanup() has already nulled `socket` --
 * so close() has nothing to close, and openSocket would fire later and reset intentionalClose,
 * resurrecting a connection the consumer deliberately shut down.
 */
let reconnectTimer = null;

/**
 * Settler for the connect currently in flight, or null.
 *
 * Held so a socket that closes before it ever opens can fail its connect immediately instead of
 * leaving the caller to wait out the full connect timeout -- and, worse, leaving `connectPromise`
 * set, so a later connect() would hand back the stale promise without opening anything.
 */
let pendingSettle = null;
let messageHandlers = [];
let stateHandlers = [];

/**
 * True once the relay has proved absent, after which the session uses the direct URL.
 *
 * This exists because the PWA ships independently of the StationBuddy release train: a station
 * will have devices whose native build has no relay at all, and enabling the feature there would
 * otherwise retry loopback forever and take out offline mode entirely on those devices. Only a
 * failure to reach the relay sets this. An upstream failure reported *by* the relay does not --
 * see the close handler.
 */
let relayUnavailable = false;


/** Whether the current connection is going through the relay. */
let viaRelay = false;

function requireInit() {
    if (!config) {
        throw new Error("[ThinClientTransport] init() must be called before use");
    }
}

function logPrefix() {
    return `[${config ? config.metricPrefix : "ThinClient"}-TC]`;
}

function metric(name) {
    return `${config.metricPrefix}.ThinClient.${name}`;
}

function publishCount(name) {
    try {
        PUMAMetricHelper.publishCountToDolphinCSM(metric(name));
    } catch (e) {
        // Metrics must never take down the transport that emits them.
    }
}

function publishLatency(startTime, name) {
    try {
        PUMAMetricHelper.publishLatencyToDolphinCSM(startTime, metric(name));
    } catch (e) {
        // As above.
    }
}

/**
 * Configures the transport. Safe to call again to reconfigure; registered handlers survive so
 * a consumer can subscribe before deciding its options.
 *
 * CALLER OBLIGATION: because handlers survive, a consumer that re-initialises AND re-subscribes
 * must first invoke the unsubscribers returned by {@link onMessage}/{@link onStateChange}.
 * Otherwise it accumulates one handler per cycle and every inbound frame is dispatched N times --
 * which for an RPC consumer means N replies to one request. There is no bulk detach on purpose:
 * this module cannot tell a leaked handler from a deliberately long-lived one.
 *
 * @param {object} options
 * @param {string} options.stage           Constants.Stage value. Required -- this package
 *                                         cannot resolve the stage itself.
 * @param {string} options.region          Region code. Required, same reason.
 * @param {string} options.metricPrefix    Namespaces this consumer's metrics and log lines.
 * @param {boolean} [options.useRelay]     Consider StationBuddy's relay. Default false.
 * @param {boolean} [options.keepalive]    Run the ping/pong heartbeat. Default true.
 * @param {function} [options.shouldReconnect] Predicate consulted on every close. Default true.
 */
function init(options) {
    const opts = options || {};
    if (!opts.stage || !opts.region) {
        throw new Error("[ThinClientTransport] init requires stage and region");
    }
    if (!opts.metricPrefix) {
        throw new Error("[ThinClientTransport] init requires metricPrefix");
    }
    // Reconfiguring is only honest if what the old config started is stopped. Without this the
    // new options never take effect while a socket is live: connect() short-circuits on an OPEN
    // socket and reports success, and startPing reads keepalive once, so the interval keeps
    // running under the old setting. Handlers are detached before closing so the teardown does
    // not schedule a reconnect or emit a state transition attributed to the new config.
    // Before discardConnection, because failing the in-flight connect makes its rejection reach the
    // reconnect chain's .catch -- and that microtask runs after this function has returned with the
    // new config. openSocket had set intentionalClose false, so without this the chain would consult
    // the NEW predicate and arm a socket the caller never asked for, contradicting the contract
    // above. A later connect() clears the flag again in openSocket, so normal use is unaffected.
    intentionalClose = true;
    discardConnection();
    config = {
        stage: opts.stage,
        region: opts.region,
        metricPrefix: opts.metricPrefix,
        useRelay: opts.useRelay === true,
        keepalive: opts.keepalive !== false,
        shouldReconnect: typeof opts.shouldReconnect === "function" ? opts.shouldReconnect : () => true
    };
    reconnectDelay = RECONNECT_INTERVAL_MS;
    relayUnavailable = false;
}

/**
 * Approach 2: route the socket through StationBuddy instead of resolving the thin-client
 * hostname in the browser.
 *
 * During a station WAN outage the browser cannot resolve the thin-client hostname, and it has
 * no way to connect to a remembered address while still presenting the hostname for TLS -- the
 * certificate is bound to the name. Native can, via OkHttp with a pinned-DNS resolver, so the
 * outbound wss:// connection and its TLS session move into StationBuddy and this socket becomes
 * plaintext loopback. Frames are relayed verbatim, so everything layered on top works unchanged
 * over either transport.
 */
function resolveUrl() {
    // No PWA-side feature check. `OosRelayEnabled` is read natively from Application.onCreate(),
    // and OfflineFallbackStarter.start() returns before binding anything when it is off -- so a
    // station that is not enabled yet presents as a refused loopback connect, which the
    // relayUnavailable latch below turns into the direct URL for the rest of the session.
    //
    // A PWA-side gate cannot work here even in principle: it would have to be readable during an
    // outage, when the feature list cannot be fetched. That is what the localStorage cache existed
    // to paper over, and its net effect was only "the relay is on if it was on at the last online
    // boot" -- the live flag was never the value consulted when it mattered.
    if (config.useRelay && !relayUnavailable) {
        Logger.log.info(`${logPrefix()} Using native pinned-DNS relay`);
        return RELAY_URL;
    }
    return getThinClientUrl(config.stage, config.region);
}

/**
 * Builds the socket and wires its handlers. Synchronous; leaves `socket` null when no URL
 * could be built, which {@link connect} reports as TC_CONNECT_FAILED.
 */
function openSocket() {
    if (socket) {
        return;
    }
    requireInit();
    let url;
    try {
        url = resolveUrl();
    } catch (e) {
        Logger.log.error(`${logPrefix()} Cannot build a thin-client URL: ${e.message}`);
        publishCount("UrlBuildFailure");
        return;
    }

    intentionalClose = false;
    viaRelay = url === RELAY_URL;
    Logger.log.info(`${logPrefix()} Attempting WS connection to ${viaRelay ? "relay" : "thin client"}`);
    try {
        socket = new WebSocket(url);
    } catch (e) {
        // The constructor throws SYNCHRONOUSLY on a URL the browser rejects (SyntaxError) or a
        // blocked port (SecurityError). openSocket and establishConnection are both synchronous, so
        // an escaping throw would surface from connect() as an exception rather than the documented
        // rejected promise -- and from the reconnect timer it would escape during the call, before
        // `.catch` is attached, leaving an uncaught error in the setTimeout callback and the chain
        // dead with nothing armed. Leaving `socket` null instead (the assignment never happened)
        // makes establishConnection report TC_CONNECT_FAILED like any other failure to produce one.
        Logger.log.error(`${logPrefix()} Cannot open a thin-client socket: ${e.message}`);
        publishCount(viaRelay ? "Relay.SocketOpenFailure" : "SocketOpenFailure");
        return;
    }

    /**
     * The socket these handlers belong to, so a superseded one cannot act on its replacement.
     *
     * Events are delivered on a later task, so a socket that has already been abandoned can still
     * deliver open/error/message/close afterwards -- and by then the module may have opened a new
     * socket. Without this, the abandoned socket's onclose ran cleanup() on the LIVE socket and
     * reported CLOSED for a connection that was up. Reachable today: the patched onerror settles
     * connect() as TC_CONNECT_ERROR, a consumer retries from that rejection, and the first socket's
     * close event lands afterwards.
     *
     * `socket === null` deliberately still passes: that is the ordinary post-cleanup state, and
     * onclose must still emit CLOSED and drive the reconnect from it.
     */
    const owner = socket;
    const superseded = () => socket !== null && socket !== owner;

    /**
     * This socket's own leg and open state.
     *
     * Captured per connection because the relay conclusion below must NOT read module scope:
     * openSocket has already reset `viaRelay` for the replacement socket by the time
     * an abandoned socket's close event lands, which is precisely the ordering that matters here.
     */
    const ownerViaRelay = viaRelay;
    let ownerAnswered = false;
    let concluded = false;

    /**
     * Decides, from THIS socket alone, whether the relay is usable for the rest of the session.
     *
     * Deliberately called BEFORE the superseded() guard in both handlers. The latch exists for the
     * refused-loopback case, and that is exactly when a replacement already exists when the close
     * event arrives -- the patched onerror settles connect(), the consumer retries from that
     * rejection, and this event lands afterwards. Behind the guard the latch was never set, so
     * resolveUrl kept re-dialling loopback forever at every station not yet enabled.
     *
     * Called from BOTH onerror and onclose, and that is not redundant. A consumer's retry runs as a
     * microtask in the same task as the error event, strictly before the browser delivers close -- so
     * latching only from onclose sets the flag one attempt too late and the retry has already been
     * built against RELAY_URL. onclose is still needed for RELAY_DISABLED and for a close that
     * arrives with no preceding error. Idempotent, so both firing is harmless.
     *
     * Latching on an error is safe because it is gated on this socket never having opened: on
     * loopback that IS a refused connection, not a transient blip.
     *
     * @param {number} [code] close code, when a close event is what observed the failure.
     */
    const concludeRelay = (code) => {
        if (concluded || !ownerViaRelay) {
            return;
        }
        // Not a case: an upstream failure the relay reports (4001-4003). Those mean the relay worked
        // and its leg to the thin client did not, which implies the station is offline -- so the
        // direct hostname could not resolve either. Nor RELAY_BUSY, which is transient.
        if (ownerAnswered && code !== CLOSE_CODES.RELAY_DISABLED) {
            return;
        }
        concluded = true;
        relayUnavailable = true;
        if (ownerAnswered) {
            // Already logged by logClose with the reason it gave; do not repeat it.
            publishCount("RelayDisabled");
        } else {
            Logger.log.warn(`${logPrefix()} Relay did not answer; using the direct connection`
                + " for the rest of this session");
        }
    };

    socket.onopen = function () {
        if (superseded()) {
            return;
        }
        // Under the relay this fires when the LOOPBACK socket opens, which is before the thin
        // client has been reached. Claiming "connected to thin client" here was actively
        // misleading on device: it printed immediately before a 4002 upstream failure.
        if (ownerViaRelay) {
            ownerAnswered = true;
            // The signal that replaced the old RelayUnavailable counter. Without a PWA-side flag,
            // "the relay did not answer" fires once per session at every station not yet enabled,
            // so it cannot carry an alarm; a relay that ANSWERED is rare enough to be meaningful.
            // Alarm on this going to zero at a station that was producing it.
            publishCount("RelayConnected");
            Logger.log.info(`${logPrefix()} Connected to relay; upstream status follows`);
        } else {
            Logger.log.info(`${logPrefix()} Connected to thin client`);
        }
        reconnectDelay = RECONNECT_INTERVAL_MS;
        // An established connection must leave nothing armed behind it. A reconnect scheduled
        // before the consumer called connect() itself is now redundant, and leaving it pending
        // means it fires against whatever socket exists later.
        if (reconnectTimer) {
            clearTimeout(reconnectTimer);
            reconnectTimer = null;
        }
        notifyState({state: STATES.OPEN, viaRelay: ownerViaRelay});
        startPing();
    };

    socket.onerror = function (error) {
        Logger.log.error(`${logPrefix()} WS error: ${JSON.stringify(error)}`);
        // Namespaced by leg for the same reason RelayUnavailable was dropped: during the staged
        // rollout a refused loopback connect fires an error once per session at every station not
        // yet enabled, which would swamp a genuinely failing direct connection on one counter.
        publishCount(ownerViaRelay ? "Relay.Error" : "Error");
        // Before the guard, and before the consumer's retry microtask runs, so the retry itself
        // already resolves to the direct URL rather than being the last wasted relay attempt.
        concludeRelay();
        if (superseded()) {
            return;
        }
        cleanup();
    };

    socket.onmessage = function (event) {
        if (superseded()) {
            return;
        }
        let data;
        try {
            data = JSON.parse(event.data);
        } catch (e) {
            Logger.log.error(`${logPrefix()} onmessage parse error: ${e}`);
            return;
        }
        // Keepalive is the transport's own business and is never handed to consumers.
        if (data && data.processPath === PROCESS_PATH.PONG) {
            handlePong(data);
            return;
        }
        dispatch(data, event);
    };

    socket.onclose = function (event) {
        const code = event && event.code;
        const reason = event && event.reason;
        // Counter, log and relay conclusion all run even for a superseded socket: the close really
        // happened, and suppressing the counter under-counted exactly the population the leg
        // namespacing was added to measure. What the guard must prevent is mutating state that now
        // belongs to the replacement -- so everything below it stays below it.
        publishCount(`${ownerViaRelay ? "Relay." : ""}Close.${code || "unknown"}`);
        logClose(code, reason);
        concludeRelay(code);
        if (superseded()) {
            return;
        }

        // Closed before it ever opened: fail the in-flight connect now rather than making the
        // caller wait out the connect timeout.
        if (pendingSettle) {
            const settle = pendingSettle;
            pendingSettle = null;
            settle(false);
        }

        cleanup();
        notifyState({state: STATES.CLOSED, code, reason, viaRelay: ownerViaRelay});
        scheduleReconnect();
    };
}

/**
 * 4001-4003 are the relay's own codes: the loopback hop succeeded but the relay could not reach
 * the thin client. During an outage the code plus its reason string is the entire diagnostic
 * surface available from this side, so neither is discarded.
 */
function logClose(code, reason) {
    const suffix = reason ? ` (${reason})` : "";
    switch (code) {
        case CLOSE_CODES.UPSTREAM_CLOSED:
            Logger.log.warn(`${logPrefix()} Relay reached, thin client closed the connection${suffix}`);
            break;
        case CLOSE_CODES.UPSTREAM_FAILED:
            Logger.log.error(`${logPrefix()} Relay reached, thin client unreachable`
                + ` (stale pin or TC down)${suffix}`);
            break;
        case CLOSE_CODES.NO_UPSTREAM:
            Logger.log.error(`${logPrefix()} Relay could not determine a thin-client endpoint${suffix}`);
            break;
        case CLOSE_CODES.RELAY_DISABLED:
            Logger.log.warn(`${logPrefix()} Relay is switched off on this device;`
                + ` using the direct connection for the rest of this session${suffix}`);
            break;
        case CLOSE_CODES.RELAY_BUSY:
            Logger.log.warn(`${logPrefix()} Relay is already serving another connection;`
                + ` will retry${suffix}`);
            break;
        default:
            Logger.log.warn(`${logPrefix()} WS closed: ${code}${suffix}`);
    }
}

/**
 * An explicit close() suppresses reconnect. Without this the reconnect predicate would fight
 * the caller: a consumer that closes deliberately while its predicate still reads true would
 * see the socket reopen a second later.
 */
function scheduleReconnect() {
    if (intentionalClose) {
        return;
    }
    let wanted;
    try {
        wanted = config.shouldReconnect() === true;
    } catch (e) {
        // A throwing predicate must not produce an unbounded reconnect loop.
        Logger.log.error(`${logPrefix()} shouldReconnect threw; not reconnecting: ${e}`);
        return;
    }
    if (!wanted) {
        return;
    }
    // Already armed: do nothing at all, and in particular do NOT advance the backoff.
    //
    // Two things reach here for a single failure. A failed connect fires onerror and then onclose:
    // the patched onerror settles the promise, whose .catch re-drives the chain, and onclose then
    // calls this again. Clearing and re-arming would advance the backoff twice per failure, so the
    // 30s cap arrived in half the expected number of failures -- on the majority path during
    // rollout, since a station that is not enabled yet refuses the loopback connect.
    //
    // Returning also preserves the one-armed-timer invariant. Overwriting the handle orphaned the
    // previous timer, and close() can cancel only the handle it can see, so the orphan fired later,
    // reset intentionalClose in openSocket and resurrected a connection the consumer shut down.
    if (reconnectTimer) {
        return;
    }
    reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        // Through establishConnection, not a bare openSocket, so the attempt carries the same
        // CONNECT_TIMEOUT_MS bound and the same synchronous teardown a caller-initiated connect
        // gets. Its rejection is also the single place the chain is re-driven when no close event
        // ever arrives: a URL that could not be built (resolveUrl throws while the station code is
        // unknown) rejects TC_CONNECT_FAILED, and a socket wedged in CONNECTING rejects
        // TC_CONNECT_TIMEOUT. Without that the chain would stop dead and the transport would stay
        // silent for the rest of the process while shouldReconnect still reads true.
        //
        // Unconditional: scheduleReconnect itself ignores the call when a timer is already armed,
        // which is what keeps one failure to one backoff advance when onclose re-drives too.
        establishConnection().catch(() => {
            scheduleReconnect();
        });
    }, reconnectDelay);
    reconnectDelay = Math.min(reconnectDelay * 2, RECONNECT_MAX_INTERVAL_MS);
}

function dispatch(data, event) {
    messageHandlers.forEach(handler => {
        try {
            handler(data, event);
        } catch (e) {
            // One misbehaving subscriber must not starve the others.
            Logger.log.error(`${logPrefix()} message handler threw: ${e}`);
        }
    });
}

function notifyState(state) {
    stateHandlers.forEach(handler => {
        try {
            handler(state);
        } catch (e) {
            Logger.log.error(`${logPrefix()} state handler threw: ${e}`);
        }
    });
}

function cleanup() {
    socket = null;
    clearInterval(pingInterval);
    pingInterval = null;
    clearTimeout(pongTimeout);
    pongTimeout = null;
    missedPongCount = 0;
}

/**
 * Drops any live socket, pending reconnect and in-flight connect, leaving no timer running and
 * no handler attached. Used by init() so a reconfigure cannot inherit the old config's work.
 *
 * Handlers are detached BEFORE close() so onclose cannot schedule a reconnect or report a state
 * transition, and any awaiting connect() is rejected rather than left to time out against a
 * socket that no longer exists.
 */
function discardConnection() {
    if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
    }
    if (pendingSettle) {
        const settle = pendingSettle;
        pendingSettle = null;
        // silent: every caller of discardConnection is either a deliberate teardown (init, close) or
        // runs after the connect already settled (handleMissedPong), so none of them is a connect
        // attempt failing on its own. The caller still gets TC_CONNECT_ERROR; what it must not do is
        // land on the connect-scoped counter.
        settle(false, {silent: true});
    }
    if (socket) {
        socket.onopen = null;
        socket.onerror = null;
        socket.onclose = null;
        socket.onmessage = null;
        try {
            socket.close();
        } catch (e) {
            // A socket that refuses to close must not stop the reconfigure.
        }
    }
    cleanup();
}

function handlePong(data) {
    const id = data && data.payload && data.payload.requestId;
    if (id !== latestPingRequestId) {
        return;
    }
    clearTimeout(pongTimeout);
    pongTimeout = null;
    missedPongCount = 0;
}

/**
 * Records a missed pong and closes once MAX_MISSED_PONGS is reached.
 *
 * Called ONLY from the pong-timeout callback: PONG_TIMEOUT_MS is shorter than PING_INTERVAL_MS, so
 * that timeout always fires -- and counts the miss -- before the next tick is due. If those two
 * constants are ever reordered, a miss would go uncounted and the tick must observe it again.
 */
function handleMissedPong() {
    missedPongCount++;
    pongTimeout = null;
    if (missedPongCount >= MAX_MISSED_PONGS) {
        Logger.log.error(`${logPrefix()} Missed ${missedPongCount} consecutive pongs; closing`);
        publishCount("MissedPongClose");
        // Tear down synchronously rather than leaving it to onclose.
        //
        // The heartbeat is still running at this point, and a real WebSocket dispatches close on a
        // LATER task -- so the next tick found `pongTimeout` null (this function nulls it above),
        // skipped the guard that would have re-entered here, armed a fresh pongTimeout, and then
        // re-entered anyway with missedPongCount still at the limit. That republished
        // MissedPongClose and re-closed the socket once per interval until the close event landed.
        //
        // discardConnection detaches the handlers first, which also stops the late close event from
        // cleaning up a socket that the reconnect below may already have opened. It leaves the
        // CLOSED transition and the reconnect to be driven from here, as the connect-timeout
        // teardown does for the same reason.
        discardConnection();
        // Module-scope viaRelay, deliberately: this runs from the heartbeat, which only exists for
        // the socket that is live now, and module functions cannot see openSocket's closure.
        notifyState({state: STATES.CLOSED, viaRelay});
        scheduleReconnect();
    }
}

function startPing() {
    if (!config.keepalive) {
        return;
    }
    pingInterval = setInterval(() => {
        // No "is a pong still outstanding?" check: PONG_TIMEOUT_MS is shorter than PING_INTERVAL_MS,
        // so the pong timeout has always fired -- and already counted the miss -- before this tick is
        // due. Such a check was unreachable, and unreachable code in this lifecycle is a liability
        // rather than insurance. See handleMissedPong if those constants are ever reordered.
        latestPingRequestId = uuidv4();
        const ping = {
            processPath: PROCESS_PATH.PING,
            payload: {requestId: latestPingRequestId, dolphinMessageSendTimestamp: Date.now()}
        };
        if (socket && socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify(ping));
        }
        pongTimeout = setTimeout(() => {
            handleMissedPong();
        }, PONG_TIMEOUT_MS);
    }, PING_INTERVAL_MS);
}

/**
 * Opens the socket with a bounded connect, resolving when it is open.
 *
 * Module-level rather than inline in connect() so the RECONNECT path can use it too. A bare
 * openSocket() there has no CONNECT_TIMEOUT_MS bound: a socket that hangs in CONNECTING is never
 * abandoned, onclose is the only other thing that re-drives scheduleReconnect, and keepalive does
 * not start until onopen -- so the transport would go silent until the browser's own much longer
 * TCP timeout, with no reconnect pending. That is the failure CONNECT_TIMEOUT_MS exists to prevent
 * on the first attempt, and it applies just as much to every attempt after it.
 *
 * All concurrent callers share one in-flight promise, so a second call during CONNECTING awaits
 * the same result rather than replacing the first caller's resolver.
 *
 * @returns {Promise<void>} Rejects TC_CONNECT_TIMEOUT, TC_CONNECT_ERROR or TC_CONNECT_FAILED.
 */
function establishConnection() {
    requireInit();
    // Must stay ahead of the connectPromise guard: patching onopen on an already-OPEN socket
    // never settles, and the connect timeout would then close a live connection.
    if (socket && socket.readyState === WebSocket.OPEN) {
        return Promise.resolve();
    }
    if (connectPromise) {
        return connectPromise;
    }
    // Built BEFORE the promise exists, deliberately. This used to run inside the executor, which
    // is called synchronously during `connectPromise = new Promise(...)` -- so the `connectPromise
    // = null` on the no-socket path was written before the outer assignment and then overwritten
    // by the rejected promise itself. Every later call returned that stale rejection without
    // opening anything, permanently. Nothing can dispatch an event between here and the executor
    // below, so the ordering is otherwise equivalent.
    const startTime = Date.now();
    openSocket();
    if (!socket) {
        // NOT namespaced by leg, deliberately: resolveUrl may have thrown before viaRelay was
        // assigned, so the module value here can belong to the previous attempt. The leg is
        // genuinely unknown on this path and a prefix would mislabel it.
        publishCount("ConnectFailed");
        return Promise.reject(new Error("TC_CONNECT_FAILED"));
    }
    // Captured per attempt: these counters settle later, by which time a subsequent openSocket may
    // have moved the module value to the other leg.
    const attemptViaRelay = viaRelay;
    connectPromise = new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
            connectPromise = null;
            // Claim the settle before closing, so the onclose below does not double-settle.
            pendingSettle = null;
            // Tear down the still-connecting socket SYNCHRONOUSLY. Detaching the patched
            // handlers stops a late open starting a heartbeat on a socket the caller was told
            // had failed; detaching onclose/onmessage and calling cleanup() ourselves is what
            // makes the teardown complete by the time the caller learns of the failure. A real
            // WebSocket dispatches close on a later task, so waiting for onclose to null the
            // reference leaves the zombie live for the whole rejection handler -- and a retry
            // there, the natural place for one, trips openSocket's `if (socket) return` and
            // re-patches a dead socket that can only settle as TC_CONNECT_ERROR.
            if (socket) {
                socket.onopen = null;
                socket.onerror = null;
                socket.onclose = null;
                socket.onmessage = null;
                try {
                    socket.close();
                } catch (e) {
                    // A socket that refuses to close must not strand the teardown.
                }
                cleanup();
            }
            publishCount(attemptViaRelay ? "Relay.ConnectTimeout" : "ConnectTimeout");
            // Detaching onclose above removed the only caller of both of these. Without them a
            // caller-initiated connect that times out leaves nothing armed and emits no state
            // transition, so a consumer tracking connectivity through onStateChange never learns
            // the attempt ended -- the silent transport this bound exists to prevent, moved to the
            // entry path. Safe on the reconnect path too: the .catch below calls scheduleReconnect
            // again and it returns early because this call has already armed the timer.
            notifyState({state: STATES.CLOSED, viaRelay: attemptViaRelay});
            scheduleReconnect();
            reject(new Error("TC_CONNECT_TIMEOUT"));
        }, CONNECT_TIMEOUT_MS);

        // At most once. The patched onopen/onerror below are never detached, so they outlive the
        // connect: a live socket emitting `error` mid-session runs the real handler and then reaches
        // here a second time. The reject is a harmless no-op on an already-settled promise, but
        // publishCount("ConnectError") is not -- it would charge a mid-session failure to a
        // connect-scoped counter. The patched onerror is kept attached deliberately, because
        // detaching it means restoring a handler on a socket cleanup() may already have dropped.
        let done = false;
        const settled = (ok, options) => {
            if (done) {
                return;
            }
            done = true;
            clearTimeout(timer);
            connectPromise = null;
            pendingSettle = null;
            if (ok) {
                publishLatency(startTime,
                    attemptViaRelay ? "Relay.ConnectLatency" : "ConnectLatency");
                resolve();
            } else {
                if (!(options && options.silent)) {
                    publishCount(attemptViaRelay ? "Relay.ConnectError" : "ConnectError");
                }
                reject(new Error("TC_CONNECT_ERROR"));
            }
        };

        // openSocket already ran above and assigned the handlers; patch them to settle the
        // promise after the real handler has run.
        pendingSettle = settled;
        const realOpen = socket.onopen;
        const realError = socket.onerror;
        socket.onopen = function () {
            if (realOpen) {
                realOpen.apply(this, arguments);
            }
            settled(true);
        };
        socket.onerror = function () {
            if (realError) {
                realError.apply(this, arguments);
            }
            settled(false);
        };
    });
    return connectPromise;
}

export default {

    init,

    /**
     * Opens the socket, resolving when it is open. See establishConnection.
     *
     * @returns {Promise<void>} Rejects TC_CONNECT_TIMEOUT, TC_CONNECT_ERROR or TC_CONNECT_FAILED.
     */
    connect() {
        requireInit();
        return establishConnection();
    },

    /**
     * Sends a frame. Objects are JSON-stringified; strings are sent as-is.
     *
     * @throws {Error} TC_CONN_NOT_OPEN if the socket is not open. Deliberately loud -- silently
     *                 dropping a scan would leave the associate with no feedback.
     */
    send(message) {
        requireInit();
        if (!socket || socket.readyState !== WebSocket.OPEN) {
            throw new Error("TC_CONN_NOT_OPEN");
        }
        socket.send(typeof message === "string" ? message : JSON.stringify(message));
    },

    /**
     * Subscribes to inbound frames. Multiple handlers are supported because a consumer can have
     * several independent readers (Stow routes lidar and config events separately from its own
     * acknowledgements). Pong frames are consumed by the keepalive and never delivered here.
     *
     * @param {function} handler Called with (parsedFrame, rawEvent).
     * @returns {function} Unsubscribe.
     */
    onMessage(handler) {
        messageHandlers.push(handler);
        return () => {
            messageHandlers = messageHandlers.filter(h => h !== handler);
        };
    },

    /**
     * Subscribes to open/close transitions.
     *
     * @param {function} handler Called with {state, code, reason}.
     * @returns {function} Unsubscribe.
     */
    onStateChange(handler) {
        stateHandlers.push(handler);
        return () => {
            stateHandlers = stateHandlers.filter(h => h !== handler);
        };
    },

    isConnected() {
        return !!(socket && socket.readyState === WebSocket.OPEN);
    },

    /**
     * Closes the socket and suppresses the reconnect that would otherwise follow, including one
     * already waiting out its backoff. Cancelling the pending timer is not belt-and-braces: at
     * that point `socket` is already null, so there is nothing for the close below to act on, and
     * openSocket would fire later and reset `intentionalClose` itself.
     */
    close() {
        const wasLive = !!socket;
        intentionalClose = true;
        // discardConnection rather than a bare socket.close(): it cancels a pending reconnect,
        // fails any in-flight connect, detaches all four handlers and calls cleanup(). A real
        // WebSocket dispatches close on a LATER task, so relying on onclose here left the module
        // referencing the dying socket and the heartbeat running -- a connect() in the same turn
        // then hit openSocket's `if (socket) return`, re-patched it, and could only settle as
        // TC_CONNECT_ERROR with intentionalClose still true so nothing could recover it.
        discardConnection();
        // Detaching onclose also removed the CLOSED transition a consumer used to receive here.
        // Module-scope viaRelay again, and correct: the caller is closing the connection live now.
        if (wasLive) {
            notifyState({state: STATES.CLOSED, viaRelay});
        }
    }
};
